import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../../db/firebase";
import { useTranslation } from "react-i18next";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import { HiOutlineXMark, HiOutlineCog6Tooth } from "react-icons/hi2";

const inputBase =
  "w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-all";
const labelClass =
  "block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const [orgName, setOrgName] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const fetchOrganizationDetails = async () => {
      if (auth.currentUser) {
        setLoading(true);
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const orgDocRef = doc(db, "organizations", userData.organizationId);
          const orgDoc = await getDoc(orgDocRef);
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            setOrgName(orgData.orgName ?? "");
            setContactInfo(orgData.contactInfo ?? "");
            setLanguage(orgData.language || "en");
          }
        }
        setLoading(false);
      }
    };
    fetchOrganizationDetails();
  }, [isOpen]);

  const handleSave = async () => {
    if (!auth.currentUser) return;
    setSaving(true);
    try {
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const orgDocRef = doc(db, "organizations", userData.organizationId);
        await updateDoc(orgDocRef, {
          orgName: orgName ?? "",
          contactInfo: contactInfo ?? "",
          language: language ?? "en",
        });
        i18n.changeLanguage(language);
        alert(t("organizationSettingsUpdated"));
        onClose();
      }
    } catch (err) {
      console.error("Error saving organization:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} contentClassName="sm:max-w-md">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-white dark:from-gray-800 dark:to-gray-800/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shadow-sm">
              <HiOutlineCog6Tooth className="w-6 h-6" strokeWidth={2} />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
              {t("organizationSettings")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
                  <div className="h-12 rounded-xl bg-gray-200 dark:bg-gray-700 animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <label htmlFor="modal-orgName" className={labelClass}>
                  {t("orgName")}
                </label>
                <input
                  type="text"
                  id="modal-orgName"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder={t("orgName")}
                  required
                  className={inputBase}
                />
              </div>
              <div>
                <label htmlFor="modal-contactInfo" className={labelClass}>
                  {t("contactInfo")}
                </label>
                <input
                  type="text"
                  id="modal-contactInfo"
                  value={contactInfo}
                  onChange={(e) => setContactInfo(e.target.value)}
                  placeholder={t("contactInfo")}
                  className={inputBase}
                />
              </div>
              <div>
                <label htmlFor="modal-language" className={labelClass}>
                  {t("language")}
                </label>
                <select
                  id="modal-language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className={inputBase}
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                </select>
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 rounded-xl font-medium border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t("cancel")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : null}
                  {t("save")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AnimatedModal>
  );
};

export default SettingsModal;
